<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import Settings from './settings/Index.vue';
import Tools from './tools/Index.vue';
import Connection from './connection/Index.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const tabName = computed(() => route.params.tab);
const serverId = computed(() => Number(route.params.serverId));
const server = computed(() =>
  store.getters['wgptMcpServers/getServer'](serverId.value)
);
const fetchedServerId = ref(null);
const isLoading = computed(
  () =>
    server.value.id !== serverId.value ||
    fetchedServerId.value !== serverId.value
);

const tabs = [
  { key: 'settings', name: t('WGPT_MCP_SERVERS.EDIT.TABS.SETTINGS') },
  { key: 'tools', name: t('WGPT_MCP_SERVERS.EDIT.TABS.TOOLS') },
  { key: 'connection', name: t('WGPT_MCP_SERVERS.EDIT.TABS.CONNECTION') },
];

const selectedTabIndex = computed(() =>
  Math.max(
    0,
    tabs.findIndex(tab => tab.key === tabName.value)
  )
);

const fetchServer = async () => {
  fetchedServerId.value = null;
  await store.dispatch('wgptMcpServers/show', serverId.value);
  fetchedServerId.value = serverId.value;
};

const onTabChange = index => {
  router.replace({
    name: 'wgpt_mcp_servers_edit',
    params: { ...route.params, tab: tabs[index].key },
  });
};

watch(serverId, () => {
  if (route.name === 'wgpt_mcp_servers_edit') fetchServer();
});

onMounted(() => {
  fetchServer();
});
</script>

<template>
  <div
    class="grid grid-rows-[auto_1fr] h-full flex-grow flex-shrink pr-0 pl-0 w-full min-w-0 settings"
  >
    <SettingIntroBanner :header-title="server.name">
      <woot-tabs
        class="[&_ul]:p-0 top-px relative"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
          is-compact
        />
      </woot-tabs>
    </SettingIntroBanner>

    <div
      v-if="isLoading"
      class="flex items-center justify-center h-full w-full"
    >
      <SpinnerLoader :size="28" class="text-n-blue-9" />
    </div>
    <section v-else class="w-full overflow-auto">
      <div class="max-w-7xl mx-auto w-full py-8">
        <Tools v-if="tabName === 'tools'" :server="server" />
        <Connection v-else-if="tabName === 'connection'" :server="server" />
        <Settings v-else :server="server" />
      </div>
    </section>
  </div>
</template>
